import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import api from '../../api/api';
import {
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingBag,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  Package,
  Truck,
  Store,
  UtensilsCrossed,
  Timer,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';

interface OrderAnalyticsProps {
  vendorId: number;
  startDate: string;
  endDate: string;
}

interface OrderAnalyticsData {
  // Order Counts
  totalOrders: number;
  completedOrders: number;
  cancelledOrders: number;
  pendingOrders: number;
  completionRate: number;

  // Customer Insights
  uniqueCustomers: number;
  repeatCustomers: number;
  avgOrdersPerCustomer: number;

  // Peak Hours (24-hour array)
  peakHours: {
    hour: number;
    orderCount: number;
  }[];

  // Order Status Breakdown
  statusBreakdown: {
    status: string;
    count: number;
    percentage: number;
  }[];

  // Day of Week Analysis
  busiestDay: {
    day: string;
    orderCount: number;
  };

  // Order Sources
  orderSources: {
    source: string;
    count: number;
    percentage: number;
  }[];

  // Additional Metrics
  avgPreparationTime: number; // in minutes
  avgItemsPerOrder: number;
}

const OrderAnalytics: React.FC<OrderAnalyticsProps> = ({
  vendorId,
  startDate,
  endDate
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OrderAnalyticsData | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [vendorId, startDate, endDate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/api/vendors/${vendorId}/reports/order-analytics`,
        {
          params: {
            startDate,
            endDate
          }
        }
      );
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to fetch order analytics:', err);
      setError(
        err.response?.data?.message ||
        'Failed to load analytics data. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: `${theme.primary} transparent ${theme.primary} ${theme.primary}` }}
          />
          <p style={{ color: theme.text }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="glassmorphism-card p-6 rounded-xl border"
        style={{
          backgroundColor: theme.components.card.background,
          borderColor: theme.danger,
        }}
      >
        <div className="flex items-center gap-3">
          <XCircle size={24} style={{ color: theme.danger }} />
          <div>
            <h3 className="font-semibold" style={{ color: theme.danger }}>
              Error Loading Analytics
            </h3>
            <p style={{ color: theme.text }}>{error}</p>
          </div>
        </div>
        <button
          onClick={fetchAnalytics}
          className="mt-4 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: theme.button.primary.bg,
            color: theme.button.primary.text,
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    trend?: 'up' | 'down' | 'neutral';
  }> = ({ title, value, icon, color, subtitle, trend }) => (
    <div
      className="glassmorphism-card p-6 rounded-xl border transition-all hover:shadow-lg"
      style={{
        backgroundColor: theme.components.card.background,
        borderColor: theme.components.card.border,
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium opacity-70" style={{ color: theme.text }}>
            {title}
          </p>
          <div className="flex items-baseline gap-2 mt-2">
            <h3 className="text-3xl font-bold" style={{ color: theme.text }}>
              {value}
            </h3>
            {trend && (
              <span className="flex items-center text-sm">
                {trend === 'up' && <TrendingUp size={16} style={{ color: theme.success }} />}
                {trend === 'down' && <TrendingDown size={16} style={{ color: theme.danger }} />}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs mt-1 opacity-60" style={{ color: theme.text }}>
              {subtitle}
            </p>
          )}
        </div>
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: color + '20',
            color: color,
          }}
        >
          {icon}
        </div>
      </div>
    </div>
  );

  const ProgressBar: React.FC<{
    value: number;
    max: number;
    color: string;
    label?: string;
  }> = ({ value, max, color, label }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;

    return (
      <div className="space-y-1">
        {label && (
          <div className="flex justify-between text-sm">
            <span style={{ color: theme.text }}>{label}</span>
            <span style={{ color: theme.text }} className="font-medium">
              {value}
            </span>
          </div>
        )}
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: theme.gray[200] }}
        >
          <div
            className="h-full transition-all duration-500 rounded-full"
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    );
  };

  const peakHour = data.peakHours && data.peakHours.length > 0
    ? data.peakHours.reduce(
        (max, hour) => (hour.orderCount > max.orderCount ? hour : max),
        data.peakHours[0]
      )
    : { hour: 0, orderCount: 0 };

  const maxPeakHourCount = data.peakHours && data.peakHours.length > 0
    ? Math.max(...data.peakHours.map(h => h.orderCount))
    : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div
        className="glassmorphism-card p-4 rounded-xl border"
        style={{
          backgroundColor: theme.components.card.background,
          borderColor: theme.components.card.border,
        }}
      >
        <div className="flex items-center gap-3">
          <BarChart3 size={28} style={{ color: theme.primary }} />
          <div>
            <h2 className="text-2xl font-bold" style={{ color: theme.text }}>
              Order Analytics
            </h2>
            <p className="text-sm opacity-70" style={{ color: theme.text }}>
              {startDate} to {endDate}
            </p>
          </div>
        </div>
      </div>

      {/* Order Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Orders"
          value={data.totalOrders}
          icon={<ShoppingBag size={24} />}
          color={theme.primary}
        />
        <MetricCard
          title="Completed Orders"
          value={data.completedOrders}
          icon={<CheckCircle size={24} />}
          color={theme.success}
          subtitle={`${(data.completionRate ?? 0).toFixed(1)}% completion rate`}
        />
        <MetricCard
          title="Cancelled Orders"
          value={data.cancelledOrders}
          icon={<XCircle size={24} />}
          color={theme.danger}
          subtitle={`${((data.cancelledOrders ?? 0) / (data.totalOrders ?? 1) * 100).toFixed(1)}% cancellation rate`}
        />
        <MetricCard
          title="Pending Orders"
          value={data.pendingOrders}
          icon={<AlertCircle size={24} />}
          color={theme.warning}
        />
      </div>

      {/* Customer Insights */}
      <div
        className="glassmorphism-card p-6 rounded-xl border"
        style={{
          backgroundColor: theme.components.card.background,
          borderColor: theme.components.card.border,
        }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Users size={24} style={{ color: theme.primary }} />
          <h3 className="text-xl font-bold" style={{ color: theme.text }}>
            Customer Insights
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: theme.gray[100] }}>
            <div className="text-3xl font-bold" style={{ color: theme.primary }}>
              {data.uniqueCustomers}
            </div>
            <div className="text-sm mt-1 opacity-70" style={{ color: theme.text }}>
              Unique Customers
            </div>
          </div>

          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: theme.gray[100] }}>
            <div className="text-3xl font-bold" style={{ color: theme.success }}>
              {data.repeatCustomers}
            </div>
            <div className="text-sm mt-1 opacity-70" style={{ color: theme.text }}>
              Repeat Customers
            </div>
          </div>

          <div className="text-center p-4 rounded-lg" style={{ backgroundColor: theme.gray[100] }}>
            <div className="text-3xl font-bold" style={{ color: theme.accent }}>
              {(data.avgOrdersPerCustomer ?? 0).toFixed(1)}
            </div>
            <div className="text-sm mt-1 opacity-70" style={{ color: theme.text }}>
              Avg Orders Per Customer
            </div>
          </div>
        </div>
      </div>

      {/* Peak Hours Chart */}
      <div
        className="glassmorphism-card p-6 rounded-xl border"
        style={{
          backgroundColor: theme.components.card.background,
          borderColor: theme.components.card.border,
        }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Clock size={24} style={{ color: theme.primary }} />
          <h3 className="text-xl font-bold" style={{ color: theme.text }}>
            Peak Hours Analysis
          </h3>
        </div>

        <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: theme.gray[100] }}>
          <p className="text-sm" style={{ color: theme.text }}>
            <span className="font-semibold">Busiest Hour: </span>
            {peakHour.hour}:00 - {peakHour.hour + 1}:00 with {peakHour.orderCount} orders
          </p>
        </div>

        <div className="space-y-3">
          {data.peakHours && data.peakHours.length > 0 ? (
            data.peakHours
              .filter(h => h.orderCount > 0)
              .sort((a, b) => b.orderCount - a.orderCount)
              .slice(0, 8)
              .map((hour) => (
                <ProgressBar
                  key={hour.hour}
                  value={hour.orderCount}
                  max={maxPeakHourCount}
                  color={hour.hour === peakHour.hour ? theme.primary : theme.accent}
                  label={`${hour.hour.toString().padStart(2, '0')}:00 - ${(hour.hour + 1).toString().padStart(2, '0')}:00`}
                />
              ))
          ) : (
            <p className="text-center py-4" style={{ color: theme.gray[500] }}>
              No peak hours data available
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Breakdown */}
        <div
          className="glassmorphism-card p-6 rounded-xl border"
          style={{
            backgroundColor: theme.components.card.background,
            borderColor: theme.components.card.border,
          }}
        >
          <div className="flex items-center gap-2 mb-6">
            <PieChart size={24} style={{ color: theme.primary }} />
            <h3 className="text-xl font-bold" style={{ color: theme.text }}>
              Order Status Breakdown
            </h3>
          </div>

          <div className="space-y-4">
            {data.statusBreakdown && data.statusBreakdown.length > 0 ? (
              data.statusBreakdown.map((status) => {
              let color = theme.gray[500];
              let icon = <Activity size={16} />;

              switch (status.status) {
                case 'COMPLETED':
                  color = theme.success;
                  icon = <CheckCircle size={16} />;
                  break;
                case 'CANCELLED':
                  color = theme.danger;
                  icon = <XCircle size={16} />;
                  break;
                case 'PENDING':
                case 'ACCEPTED':
                  color = theme.warning;
                  icon = <AlertCircle size={16} />;
                  break;
                case 'PREPARING':
                case 'READY_FOR_PICKUP':
                  color = theme.primary;
                  icon = <Clock size={16} />;
                  break;
              }

              return (
                <div key={status.status}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span style={{ color }}>{icon}</span>
                      <span className="text-sm font-medium" style={{ color: theme.text }}>
                        {status.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold" style={{ color: theme.text }}>
                        {status.count}
                      </span>
                      <span className="text-xs opacity-70" style={{ color: theme.text }}>
                        ({(status.percentage ?? 0).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: theme.gray[200] }}
                  >
                    <div
                      className="h-full transition-all duration-500 rounded-full"
                      style={{
                        width: `${status.percentage}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </div>
              );
            })
            ) : (
              <p className="text-center py-4" style={{ color: theme.gray[500] }}>
                No status data available
              </p>
            )}
          </div>
        </div>

        {/* Order Sources */}
        <div
          className="glassmorphism-card p-6 rounded-xl border"
          style={{
            backgroundColor: theme.components.card.background,
            borderColor: theme.components.card.border,
          }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Package size={24} style={{ color: theme.primary }} />
            <h3 className="text-xl font-bold" style={{ color: theme.text }}>
              Order Sources
            </h3>
          </div>

          <div className="space-y-4">
            {data.orderSources && data.orderSources.length > 0 ? (
              data.orderSources.map((source) => {
              let color = theme.primary;
              let icon = <Store size={16} />;

              switch (source.source) {
                case 'DINE_IN':
                  color = theme.primary;
                  icon = <UtensilsCrossed size={16} />;
                  break;
                case 'TAKEAWAY':
                  color = theme.success;
                  icon = <Package size={16} />;
                  break;
                case 'DELIVERY':
                  color = theme.accent;
                  icon = <Truck size={16} />;
                  break;
              }

              return (
                <div key={source.source}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span style={{ color }}>{icon}</span>
                      <span className="text-sm font-medium" style={{ color: theme.text }}>
                        {source.source.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold" style={{ color: theme.text }}>
                        {source.count}
                      </span>
                      <span className="text-xs opacity-70" style={{ color: theme.text }}>
                        ({(source.percentage ?? 0).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: theme.gray[200] }}
                  >
                    <div
                      className="h-full transition-all duration-500 rounded-full"
                      style={{
                        width: `${source.percentage}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </div>
              );
            })
            ) : (
              <p className="text-center py-4" style={{ color: theme.gray[500] }}>
                No order source data available
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div
          className="glassmorphism-card p-6 rounded-xl border"
          style={{
            backgroundColor: theme.components.card.background,
            borderColor: theme.components.card.border,
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Timer size={24} style={{ color: theme.primary }} />
            <h3 className="text-xl font-bold" style={{ color: theme.text }}>
              Average Preparation Time
            </h3>
          </div>
          <div className="text-center py-6">
            <div className="text-5xl font-bold" style={{ color: theme.primary }}>
              {(data.avgPreparationTime ?? 0).toFixed(0)}
            </div>
            <div className="text-sm mt-2 opacity-70" style={{ color: theme.text }}>
              minutes
            </div>
          </div>
        </div>

        <div
          className="glassmorphism-card p-6 rounded-xl border"
          style={{
            backgroundColor: theme.components.card.background,
            borderColor: theme.components.card.border,
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag size={24} style={{ color: theme.success }} />
            <h3 className="text-xl font-bold" style={{ color: theme.text }}>
              Average Items Per Order
            </h3>
          </div>
          <div className="text-center py-6">
            <div className="text-5xl font-bold" style={{ color: theme.success }}>
              {(data.avgItemsPerOrder ?? 0).toFixed(1)}
            </div>
            <div className="text-sm mt-2 opacity-70" style={{ color: theme.text }}>
              items
            </div>
          </div>
        </div>
      </div>

      {/* Busiest Day */}
      <div
        className="glassmorphism-card p-6 rounded-xl border"
        style={{
          backgroundColor: theme.components.card.background,
          borderColor: theme.components.card.border,
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={24} style={{ color: theme.primary }} />
          <h3 className="text-xl font-bold" style={{ color: theme.text }}>
            Busiest Day of Week
          </h3>
        </div>
        <div className="text-center py-4">
          <div className="text-4xl font-bold" style={{ color: theme.primary }}>
            {data.busiestDay.day}
          </div>
          <div className="text-sm mt-2 opacity-70" style={{ color: theme.text }}>
            with {data.busiestDay.orderCount} orders
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderAnalytics;
